import { Stethoscope, Shield, Check, Loader2, UserCheck, User } from "lucide-react";
import { Calendar as CalendarIcon2 } from "lucide-react";
import { format } from "date-fns";
import { it } from "date-fns/locale";

import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CommandEmpty, CommandGroup, CommandItem } from "@/components/ui/command";

// --- Patient search results ---

export const PatientSearchResults = ({ isSearching, patients, searchQuery, selectedPatient, onSelect }) => {
  if (isSearching) {
    return (
      <div className="py-6 text-center text-sm text-slate-500">
        <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
        Ricerca in corso...
      </div>
    );
  }
  if (patients.length === 0 && searchQuery.length >= 2) {
    return <CommandEmpty>Nessun paziente trovato.</CommandEmpty>;
  }
  if (patients.length === 0) {
    return (
      <div className="py-6 pl-9 text-left text-sm text-slate-500">
        Digita almeno 2 caratteri per cercare
      </div>
    );
  }
  return (
    <CommandGroup heading="Pazienti">
      {patients.map((patient) => (
        <CommandItem
          key={patient.id}
          value={`${patient.lastName} ${patient.firstName}`}
          onSelect={() => onSelect(patient)}
          className="cursor-pointer"
          data-testid={`patient-${patient.id}`}
        >
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
              <User className="w-4 h-4 text-blue-600" />
            </div>
            <div>
              <p className="font-medium text-slate-800">
                {patient.lastName} {patient.firstName}
              </p>
              <p className="text-xs text-slate-500">
                {patient.email || patient.phone || "Nessun contatto"}
              </p>
            </div>
          </div>
          {selectedPatient?.id === patient.id && (
            <Check className="ml-auto w-4 h-4 text-blue-600" />
          )}
        </CommandItem>
      ))}
    </CommandGroup>
  );
};

// --- Doctor selector ---

export const DoctorSelect = ({ value, onChange, doctors, isLoading }) => (
  <div className="space-y-2 opacity-0 animate-fade-in animation-delay-300">
    <Label className="label-custom flex items-center gap-2">
      <Stethoscope className="w-4 h-4" />
      Medico
    </Label>
    <Select value={value} onValueChange={onChange} disabled={isLoading}>
      <SelectTrigger className="h-12 custom-input rounded-xl" data-testid="select-medico">
        <SelectValue placeholder={isLoading ? "Caricamento..." : "Seleziona un medico"} />
      </SelectTrigger>
      <SelectContent className="select-content">
        {doctors.map((doctor) => (
          <SelectItem key={doctor.id} value={doctor.id} data-testid={`doctor-${doctor.id}`} disabled={!!doctor._ghost}>
            {doctor.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  </div>
);

// --- Insurance selector ---

export const AssistantSelect = ({ value, onChange, assistants, isLoading }) => (
  <div className="space-y-2 opacity-0 animate-fade-in animation-delay-300">
    <Label className="label-custom flex items-center gap-2">
      <UserCheck className="w-4 h-4" />
      Assistente
    </Label>
    <Select value={value} onValueChange={onChange} disabled={isLoading}>
      <SelectTrigger className="h-12 custom-input rounded-xl" data-testid="select-assistente">
        <SelectValue placeholder={isLoading ? "Caricamento..." : "Seleziona un assistente"} />
      </SelectTrigger>
      <SelectContent className="select-content">
        {assistants.map((a) => (
          // BUG FIX 17/06/2026: il ghost item NON deve essere `disabled`
          // — se l'assistente salvato sull'opp non è nella lista corrente
          // (es. paginazione GHL, ex-dipendente con tag rimosso), prima
          // disabilitavamo l'item → su alcune versioni Radix il trigger
          // mostrava il placeholder anziché il nome dell'assistente, e
          // l'utente era costretto a ri-selezionare. Ora il ghost resta
          // selezionabile: l'utente può scegliere se mantenere o cambiare.
          <SelectItem key={a.id} value={a.id} data-testid={`assistant-${a.id}`}>
            {a.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  </div>
);

// --- Insurance selector ---

export const InsuranceSelect = ({ value, onChange, insurances, isLoading }) => (
  <div className="space-y-2 opacity-0 animate-fade-in animation-delay-500">
    <Label className="label-custom flex items-center gap-2">
      <Shield className="w-4 h-4" />
      Assicurazione
    </Label>
    <Select value={value} onValueChange={onChange} disabled={isLoading}>
      <SelectTrigger className="h-12 custom-input rounded-xl" data-testid="select-assicurazione">
        <SelectValue placeholder={isLoading ? "Caricamento..." : "Seleziona un'assicurazione"} />
      </SelectTrigger>
      <SelectContent className="select-content">
        {insurances.map((ins) => (
          <SelectItem key={ins.id} value={ins.id} data-testid={`insurance-${ins.id}`} disabled={!!ins._ghost}>
            {ins.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  </div>
);

// --- Date picker ---

export const DatePickerField = ({ value, onChange, open, onOpenChange, label }) => (
  <div className="space-y-2 opacity-0 animate-fade-in animation-delay-400">
    <Label className="label-custom flex items-center gap-2">
      <CalendarIcon2 className="w-4 h-4" />
      {label || "Data inserimento piano di cura"}
    </Label>
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          data-testid="date-picker"
          className="w-full h-12 justify-start text-left font-normal custom-input hover:bg-slate-50 rounded-xl"
        >
          <CalendarIcon2 className="mr-2 h-4 w-4 text-blue-600" />
          {value ? (
            <span className="text-slate-800">{format(value, "PPP", { locale: it })}</span>
          ) : (
            <span className="text-slate-400">Seleziona una data</span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 popover-content-custom" align="start">
        <Calendar
          mode="single"
          selected={value}
          onSelect={(date) => { onChange(date); onOpenChange(false); }}
          locale={it}
          className="calendar-custom rounded-xl"
          data-testid="calendar"
        />
      </PopoverContent>
    </Popover>
  </div>
);
